import { computeDailyMetrics } from 'domains/shifts/components/components/performance-overview/utils/computeDailyMetrics'
import { countScheduledDevicesForWeek } from 'domains/shifts/components/components/performance-overview/utils/countScheduledDevices'
import { type ShopperShiftsPerformanceMetrics } from 'domains/shifts/components/components/performance-overview/utils/types'
import { computeStaffingLevels } from 'domains/shifts/components/components/staffing-levels/utils/computeStaffingLevels'
import { type StaffingDataForWeek } from 'domains/shifts/components/components/weekly-staffing/utils/types'
import { type ShiftType } from 'domains/shifts/components/types'
import { type WarehouseEarlyPickingHour } from 'domains/shifts/components/utils/fetchEarlyHoursForWarehouse'
import { calculateActualStoreHours } from 'domains/shifts/components/utils/shiftsUtils'
import { calculateMatchRate } from 'domains/shifts/components/components/performance-overview/utils/calculateMatchRate'

export const computeWeeklyMetrics = ({
  shiftType,
  staffingDataForWeek,
  warehouseEarlyHours,
  useDevicePrioritization,
  shouldCalculateMatchRate,
  isRunnerDevicePrioritizationEnabled,
}: {
  shiftType: ShiftType
  staffingDataForWeek: StaffingDataForWeek
  warehouseEarlyHours: WarehouseEarlyPickingHour
  useDevicePrioritization: boolean
  shouldCalculateMatchRate: boolean
  isRunnerDevicePrioritizationEnabled: boolean
}): ShopperShiftsPerformanceMetrics => {
  if (!staffingDataForWeek) {
    return {
      hoursScheduled: undefined,
      hoursRecommended: undefined,
      hoursUnderstaffed: undefined,
      hoursOverstaffed: undefined,
      scheduleGaps: undefined,
      scheduledDevices: undefined,
      matchRate: shouldCalculateMatchRate ? null : undefined,
    }
  }

  const metrics: ShopperShiftsPerformanceMetrics = {
    hoursRecommended: 0,
    hoursScheduled: 0,
    hoursUnderstaffed: 0,
    hoursOverstaffed: 0,
    scheduleGaps: 0,
    scheduledDevices: countScheduledDevicesForWeek(
      staffingDataForWeek.weeklyStaffing.shoppers,
      shiftType
    ),
    matchedHours: 0,
    matchRate: shouldCalculateMatchRate ? null : undefined,
  }

  const { weeklyStaffing, weeklyActualHours } = staffingDataForWeek

  weeklyStaffing.shoppers.forEach((shoppers, idx) => {
    const regularHours = weeklyStaffing.regularHours[idx]
    const actualHours = weeklyActualHours[idx]
    const { actualStoreOpen, actualStoreClose } = calculateActualStoreHours(
      warehouseEarlyHours,
      regularHours,
      actualHours
    )

    const staffingLevels = computeStaffingLevels({
      staffingLevels: weeklyStaffing.staffingLevels[idx],
      shoppers,
      storeHours: {
        actualStoreOpen,
        actualStoreClose,
      },
      useDevicePrioritization,
      isRunnerDevicePrioritizationEnabled,
    })

    const day = computeDailyMetrics(shiftType, staffingLevels, shoppers, shouldCalculateMatchRate)

    metrics.hoursScheduled += day.hoursScheduled ?? 0
    metrics.hoursRecommended += day.hoursRecommended ?? 0
    metrics.hoursUnderstaffed += day.hoursUnderstaffed ?? 0
    metrics.hoursOverstaffed += day.hoursOverstaffed ?? 0
    metrics.scheduleGaps += day.scheduleGaps ?? 0
    metrics.matchedHours += day.matchedHours ?? 0
  })

  if (shouldCalculateMatchRate) {
    metrics.matchRate = calculateMatchRate({
      hoursRecommended: metrics.hoursRecommended,
      matchedHours: metrics.matchedHours,
    })
  }

  return metrics
}
